import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  GetStylesFunction,
  IconHeaderWithSearch,
  LoadingSpinner,
  enumContainer,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFiltersPane,
  TablePaging,
  TableSorting,
  UserFilterDto,
  WallabyCoreSchema,
  useFormatters,
  useOrganizationsByOrgSecurity,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';

import {DepartmentReportData, DepartmentReportTablesAndChart} from './DepartmentReportTablesAndChart';
import {HaiReportMeasureType, HaiTypeCategory} from './HospitalReportLayoutContainer';
import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {useAppSelector} from 'src/js/store_hooks';

export type DepartmentReportFilters = {
  dateFrom: string;
  dateTo: string;
  floatingShortcutName: ShortcutEnum | null;
  haiState: string;
  organizationIds: string[] | null;
  typeCategory: string;
  measureType: 'HAI_COUNT' | 'HOSP_WITH_HAI_COUNT';
};
type FiltersData = {
  reportOpen: boolean;
  report: DepartmentReportFilters;
  haisByTypeSort: TableSorting;
  measureSort: TableSorting;
  haisSort: TableSorting;
  haisPaging: TablePaging;
};
const DEFAULT_DEPARTMENT_REPORT_FILTERS_OPEN = true;
const DEFAULT_DEPARTMENT_REPORT_FILTERS = (_organizations: WallabyCoreSchema.components['schemas']['OrganizationFilterDto'][]) => {
  const [startOfLastMonth, endOfLastMonth] = DateUtils.lastCompletedMonth();
  return {
    dateFrom: startOfLastMonth,
    dateTo: endOfLastMonth,
    floatingShortcutName: ShortcutEnum.PREVIOUS_MONTH,
    haiState: 'ALL',
    organizationIds: null,
    typeCategory: 'HAI_TYPE',
    measureType: 'HAI_COUNT',
  } as DepartmentReportFilters;
};
const DEFAULT_HAIS_BY_TYPE_SORTING: TableSorting = {
  field: 'haiCount',
  direction: 'desc',
};
const DEFAULT_MEASURE_SORTING: TableSorting = {};
const DEFAULT_HAIS_SORTING: TableSorting = {};
interface Props {
  config: ConfigObjectType;
}
const DEFAULT_HAIS_PAGING: TablePaging = {};
export function normalizeHaiDepartmentReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}
export const DepartmentReportLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'departmentReport';
  const storageIdPrefix = '/departmentReport';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const defaultReportFilters = DEFAULT_DEPARTMENT_REPORT_FILTERS(organizations);
  const filters: FiltersData = useMemo(() => {
    return {
      reportOpen: query?.reportOpen ?? DEFAULT_DEPARTMENT_REPORT_FILTERS_OPEN,
      report: {
        ...defaultReportFilters,
        ...(query?.report ?? {}),
      },
      haisByTypeSort: query?.haisByTypeSort ?? DEFAULT_HAIS_BY_TYPE_SORTING,
      measureSort: query?.measureSort ?? DEFAULT_MEASURE_SORTING,
      haisSort: query?.haisSort ?? DEFAULT_HAIS_SORTING,
      haisPaging: query.haisPaging ?? DEFAULT_HAIS_PAGING,
    };
  }, [query, defaultReportFilters]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <DepartmentReportLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
DepartmentReportLayoutContainer.displayName = 'DepartmentReportLayoutContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
const DepartmentReportLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const haveDefaultFilters = organizations.length > 0;
  const defaultReportFilters = DEFAULT_DEPARTMENT_REPORT_FILTERS(organizations);
  // get data
  const pageTitle = 'navigation.route.departmentReport';
  const reportUrl = config.service.url.departmentReport;
  const reportParams = useTableParams(
    {
      dateFrom: filters.report.dateFrom,
      dateTo: filters.report.dateTo,
      floatingShortcutName: filters.report.floatingShortcutName,
      haiState: filters.report.haiState,
      organizationIds: filters.report.organizationIds,
      typeCategory: filters.report.typeCategory,
      measureType: filters.report.measureType,
    }
    // filters.reportSorting, // NOTE: we sort only on FE
    // {}
  );
  const {data: reportData, isLoading} = useTableData<any, DepartmentReportData>(reportUrl, reportParams, 'content', haveDefaultFilters);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(
    normalizeHaiDepartmentReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeHaiDepartmentReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const hasHaisByTypeSorting = !isEqual(omitNullsy(filters.haisByTypeSort), omitNullsy(DEFAULT_HAIS_BY_TYPE_SORTING));
  const hasMeasureSorting = !isEqual(omitNullsy(filters.measureSort), omitNullsy(DEFAULT_MEASURE_SORTING));
  const hasHaisSorting = !isEqual(omitNullsy(filters.haisSort), omitNullsy(DEFAULT_HAIS_SORTING));
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const haiReportsStateOptions = enumMap.HaiReportsState ?? [];
  // scroll
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  const haiTypeCategoryOptions = enumMap.HaiTypeCategory ?? HaiTypeCategory;
  const haiMeasureTypeOptions = enumMap.HaiReportMeasureType ?? HaiReportMeasureType;
  const {standardType} = useStandardTypes({config});

  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);

  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('reports.filters.date'),
          value: `${formatDate(filters.report.dateFrom)} - ${formatDate(filters.report.dateTo)}`,
          defaultValue: [defaultReportFilters.dateFrom, defaultReportFilters.dateTo, defaultReportFilters.floatingShortcutName],
        },
        {
          name: 'haiState',
          label: t('reports.filters.haiState'),
          value: tEnum('HaiReportsState', filters.report.haiState),
          defaultValue: defaultReportFilters.haiState,
        },
        {
          name: 'organizationIds',
          label: t('filter.hai.department'),
          value: (filters.report.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
            .join(', '),
          defaultValue: defaultReportFilters.organizationIds,
        },
        {
          name: 'typeCategory',
          label: t('reports.filters.haiTypeCategory'),
          value: tEnum('HaiTypeCategory', filters.report.typeCategory),
          defaultValue: defaultReportFilters.typeCategory,
        },
        {
          name: 'measureType',
          label: t('reports.filters.haiMeasureType'),
          value: tEnum('HaiReportMeasureType', filters.report.measureType),
          defaultValue: defaultReportFilters.measureType,
        },
      ]}
      tableFilters={filters.report}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          report: newTableFilters as DepartmentReportFilters,
          haisPaging: {
            ...filters.haisPaging,
            page: 0,
          },
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('reports.list.pageTitle')} | ${t(pageTitle)}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.reportOpen}
          toggleOpen={() =>
            changeFilters({
              reportOpen: !filters.reportOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.report,
            setFilter: (newFilter) => {
              changeFilters({
                report: newFilter as DepartmentReportFilters,
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            },
            config,
            normalizeForCompare: normalizeHaiDepartmentReportFiltersForCompare,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightMonthRangeFilter
            config={{
              name: 'date',
              ui: {label: 'reports.filters.date'},
            }}
            value={{
              startDate: filters.report.dateFrom,
              endDate: filters.report.dateTo,
              shortcut: filters.report.floatingShortcutName,
            }}
            defaultValue={
              defaultReportFilters.dateFrom && defaultReportFilters.dateTo
                ? {
                    startDate: defaultReportFilters.dateFrom,
                    endDate: defaultReportFilters.dateTo,
                    shortcut: defaultReportFilters.floatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  dateFrom: newValue?.startDate,
                  dateTo: newValue?.endDate,
                  floatingShortcutName: newValue?.shortcut,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            showShortcuts
          />
          <LightSelectFilter
            config={{
              name: 'haiState',
              ui: {label: 'reports.filters.haiState'},
            }}
            options={haiReportsStateOptions}
            value={filters.report.haiState}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiState: newValue ?? defaultReportFilters.haiState,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            isClearable={false}
          />
          <LightOrganizationFilter2
            appConfig={config}
            config={{
              name: 'organizationIds',
              ui: {label: 'filter.hai.department'},
            }}
            multi
            value={filters.report.organizationIds}
            onChange={(newOrganizationIds) => {
              changeFilters({
                report: {
                  ...filters.report,
                  organizationIds: newOrganizationIds,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            isClearable
          />
          {standardType !== 'NO_TYPES' && (
            <LightSelectFilter
              config={{
                name: 'haiTypeCategory',
                ui: {label: 'reports.filters.haiTypeCategory'},
              }}
              options={haiTypeCategoryOptions}
              value={filters.report.typeCategory}
              onChange={(newValue) => {
                changeFilters({
                  report: {
                    ...filters.report,
                    typeCategory: newValue ?? defaultReportFilters.typeCategory,
                  },
                });
              }}
              isClearable={false}
            />
          )}
          <LightSelectFilter
            config={{
              name: 'haiMeasureType',
              ui: {label: 'reports.filters.haiMeasureType'},
            }}
            options={sortByMessageKey(haiMeasureTypeOptions ?? []).reverse()}
            value={filters.report.measureType}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  measureType: newValue ?? defaultReportFilters.measureType,
                },
              });
            }}
            isClearable={false}
          />
        </LightFilterPanel>
      </div>
      <DepartmentReportTablesAndChart
        config={config}
        muiStyles={muiStyles}
        storageIdPrefix={storageIdPrefix}
        filters={filters}
        changeFilters={changeFilters}
        hasFilters={hasFilters}
        handleResetFilters={() =>
          changeFilters({
            report: defaultReportFilters,
            haisPaging: {
              ...filters.haisPaging,
              page: 0,
            },
          })
        }
        hasTopSorting={hasHaisByTypeSorting || hasMeasureSorting}
        hasHaisSorting={hasHaisSorting}
        handleResetTopSorting={() =>
          changeFilters({
            haisByTypeSort: DEFAULT_HAIS_BY_TYPE_SORTING,
            measureSort: DEFAULT_MEASURE_SORTING,
          })
        }
        handleResetHaisSorting={() =>
          changeFilters({
            haisSort: DEFAULT_HAIS_SORTING,
          })
        }
        formatters={formatters}
        isLoading={isLoading}
        reportData={reportData}
      />
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: scroll;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    flex: 0 0 auto;
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
    color: var(--dlTheme-700);
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  horizontalSplit: css`
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 32px;
  `,
});
